import React, { lazy, Suspense, useEffect, useState } from 'react';
import { PencilLine, Loader, History, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePhotoUploads } from '../hooks/usePhotoUploads';
import { useMapStore } from '@/stores/mapStore';
import { usePermissions } from '@/hooks/usePermissions';
import { TicketDetail } from '@packages/database/types';
import { findLayerForAsset } from '@/utils/map.utils';
import { cn } from '@/lib/utils';
import {
  CorrectionTimeline,
  type FieldCorrection,
  type CorrectionMode,
} from '../CorrectionTimeline';
import { useFieldCorrections } from '@/hooks/api/useFieldCorrections';
import { LinkedItemSection } from '../LinkedItemSection';
import { useMapZoom } from '@/components/map/hooks/useMapZoom';

const AssetScannerSection = lazy(() =>
  import('../AssetScannerSection').then((m) => ({
    default: m.AssetScannerSection,
  })),
);
const FieldCorrectionForm = lazy(() =>
  import('../FieldCorrectionForm').then((m) => ({
    default: m.FieldCorrectionForm,
  })),
);

interface AssetTabProps {
  ticket: TicketDetail;
  permissions: ReturnType<typeof usePermissions>;
  hideTabs: (active: boolean) => void;
}

export interface PendingCorrection {
  id: string;
  mode: CorrectionMode;
  reviewNotes?: string | null;
  proposedGeom?: any;
}

export const AssetTab = ({ ticket, permissions, hideTabs }: AssetTabProps) => {
  const { handleNewFiles } = usePhotoUploads();
  const { layers } = useMapStore();

  const [isCorrecting, setIsCorrecting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { setHighlightedCorrection, setOverlayVisible } = useMapStore();
  // const { zoomToFeature } = useMapZoom();

  const { data: fieldCorrections, isLoading } = useFieldCorrections(ticket.id);

  // const editingCorrectionId = useMapStore((s) => s.editingCorrectionId);
  // const clearEditRequest = useMapStore((s) => s.clearEditRequest);

  // useEffect(() => {
  //   if (!editingCorrectionId || !fieldCorrections?.length) return;

  //   const correction = fieldCorrections.find(
  //     (c: FieldCorrection) => c.id === editingCorrectionId,
  //   );

  //   if (correction) {
  //     handleEditFromHistory(correction);
  //     // Don't clear yet — FieldCorrectionForm will clear on save/cancel
  //   }
  // }, [editingCorrectionId, fieldCorrections]);

  const [pendingCorrection, setPendingCorrection] =
    useState<PendingCorrection | null>(null);

  const layerDef = ticket?.asset?.assetType
    ? findLayerForAsset(layers, ticket.asset.assetType)
    : null;

  const detectionKeywords: string[] =
    (layerDef?.schemaConfig as any)?.detectionKeywords ?? [];

  const handleEditFromHistory = (correction: FieldCorrection) => {
    setPendingCorrection({
      id: correction.id,
      mode: correction.correctionType,
      reviewNotes: correction.reviewNotes,
      proposedGeom: correction.proposedGeom,
    });
    setIsCorrecting(true);
    setOverlayVisible(false);
  };

  const handleCancelCorrection = () => {
    setIsCorrecting(false);
    setPendingCorrection(null);
    hideTabs(false);
  };

  // ── Field correction form ────────────────────────
  if (isCorrecting) {
    hideTabs(true);
    return (
      <Suspense fallback={<SectionLoader />}>
        <FieldCorrectionForm
          ticket={ticket}
          initialCorrection={pendingCorrection}
          onCancel={handleCancelCorrection}
        />
      </Suspense>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Linked item ──────────────────────────────────────────────────── */}
      <LinkedItemSection ticket={ticket} />

      {/* ── Scanner — only for crew editors with a linked asset ──────────── */}
      {ticket?.assetId && permissions.canEditTicket && (
        <Suspense fallback={<SectionLoader label="Loading scanner..." />}>
          <AssetScannerSection
            keywords={detectionKeywords}
            onCapture={(file) => handleNewFiles([file])}
          />
        </Suspense>
      )}

      {/* ── Field correction section — editors only ───────────────────────── */}
      {(permissions.canEditTicket || permissions.isAdmin) && (
        <div className="rounded-lg border border-border bg-muted/10 p-6 space-y-4">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              Need to Update Asset Information?
            </h3>
            <p className="text-sm text-muted-foreground">
              If the scanned asset does not match the linked record, submit a
              field update request to correct the location or metadata.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => {
                setPendingCorrection(null);
                setIsCorrecting(true);
              }}
              variant="default"
              size="sm"
            >
              <PencilLine className="w-4 h-4 mr-2" />
              Submit Field Update
            </Button>

            {(fieldCorrections?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={() => setShowHistory((p) => !p)}
                className={cn(
                  'flex items-center gap-2 text-xs font-medium rounded-md px-2.5 py-1.5 transition-colors border',
                  showHistory
                    ? 'bg-muted border-border text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
                )}
              >
                <History className="w-3.5 h-3.5" />
                <span>History</span>
                <span className="tabular-nums bg-primary/10 text-primary text-[10px] font-semibold px-1.5 py-px rounded-full">
                  {fieldCorrections.length || 0}
                </span>
                <ChevronDown
                  className={cn(
                    'w-3.5 h-3.5 transition-transform duration-200 ml-0.5',
                    showHistory && 'rotate-180',
                  )}
                />
              </button>
            )}
          </div>

          {showHistory && (
            <CorrectionTimeline
              corrections={fieldCorrections}
              onEdit={handleEditFromHistory}
              onHover={(id) => setHighlightedCorrection(id)}
              onFocus={(correction) => {
                // Zoom to the correction geometry
                // zoomToFeature(correction.proposedGeom);
                // You can use the overlay source to find the features
                setHighlightedCorrection(correction.id);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ─── Loading fallback ─────────────────────────────────────────────────────────

const SectionLoader = ({ label = 'Loading...' }: { label?: string }) => (
  <div className="rounded-lg border p-8 flex items-center justify-center gap-3 text-muted-foreground">
    <Loader className="w-4 h-4 animate-spin" />
    <span className="text-sm">{label}</span>
  </div>
);
